/**
 * Tracks the current viewport size and notifies subscribers after resize.
 */
export class WindowSize {
    #width;
    #height;
    #callbacks = new Set();
    #resizeTimer = null;
    #debounce;

    /**
     * @param {number} debounce - Delay in milliseconds before resize callbacks run.
     */
    constructor(debounce = 100) {
        this.#debounce = debounce;
        this.#setSize();

        window.addEventListener("resize", this.#handleResize);
    }

    /**
     * Current viewport size.
     *
     * @returns {{ width: number, height: number }}
     */
    get size() {
        return {
            width: this.#width,
            height: this.#height,
        };
    }

    /**
     * Current viewport width.
     *
     * @returns {number}
     */
    get width() {
        return this.#width;
    }

    /**
     * Current viewport height.
     *
     * @returns {number}
     */
    get height() {
        return this.#height;
    }

    /**
     * Checks whether the viewport width is below the given value.
     *
     * @param {number} width
     * @returns {boolean}
     */
    isBelow(width) {
        return this.#width < width;
    }

    /**
     * Runs a callback whenever the viewport size changes.
     *
     * @param {(size: { width: number, height: number }) => void} callback
     * @param {{ immediate?: boolean }} options
     * @returns {() => void} Unsubscribe callback.
     */
    onChange(callback, { immediate = false } = {}) {
        this.#callbacks.add(callback);

        if (immediate) {
            callback(this.size);
        }

        return () => this.#callbacks.delete(callback);
    }

    /**
     * Runs a callback when the viewport enters the given max-width range.
     *
     * @param {number} width
     * @param {(size: { width: number, height: number }) => void} callback
     * @param {{ immediate?: boolean }} options
     * @returns {() => void} Unsubscribe callback.
     */
    onBelow(width, callback, { immediate = false } = {}) {
        let isBelow = this.isBelow(width);

        if (immediate && isBelow) {
            callback(this.size);
        }

        return this.onChange((size) => {
            const nextIsBelow = size.width < width;

            if (nextIsBelow && nextIsBelow !== isBelow) {
                callback(size);
            }

            isBelow = nextIsBelow;
        });
    }

    /**
     * Removes listeners and pending callbacks.
     *
     * @returns {void}
     */
    destroy() {
        window.removeEventListener("resize", this.#handleResize);
        this.#callbacks.clear();
        clearTimeout(this.#resizeTimer);
    }

    #handleResize = () => {
        clearTimeout(this.#resizeTimer);

        this.#resizeTimer = setTimeout(() => {
            this.#setSize();
            this.#callbacks.forEach((callback) => callback(this.size));
        }, this.#debounce);
    };

    #setSize() {
        const { width, height } = this.#measure();

        this.#width = width;
        this.#height = height;
    }

    #measure() {
        return {
            width: window.innerWidth || document.documentElement.clientWidth,
            height: window.innerHeight || document.documentElement.clientHeight,
        };
    }
}

export default new WindowSize();
